import * as React from 'react'

import { Iconly } from 'react-iconly'
import { PiInfo } from 'react-icons/pi'

import { classNames } from '../../utils/classNames'
import Popover from './Popover'

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  selected?: string
  options: { label: string; value: string; disabled?: boolean }[]
  label?: string
  info?: string
  placeholder?: string
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>((props, ref) => {
  const { label, info, placeholder, selected, options, className, ...rest } = props

  const [value, setValue] = React.useState(selected || '')

  React.useEffect(() => {
    setValue(selected || '')
  }, [selected])

  const selectId = props.id || (typeof rest.name === 'string' ? rest.name : label)

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={selectId} className="ml-2 flex gap-1 text-th-text-secondary">
          {label}
          {props.required && !props.disabled && <div className="text-th-error">*</div>}
          {info && (
            <Popover text={info} position="bottom">
              <PiInfo className="mb-auto h-4 w-4 text-2xl text-gray-500" />
            </Popover>
          )}
        </label>
      )}
      {!props.disabled && (
        <div className="relative">
          <select
            {...rest}
            id={selectId}
            name={rest.name}
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              rest.onChange?.(e)
            }}
            className={classNames(
              'w-full appearance-none rounded-full border border-th-border px-4 py-2 pr-10 font-body text-sm',
              'bg-th-content enabled:cursor-pointer',
              !value || value === '' ? 'text-gray-300' : 'text-th-text',
              className
            )}
            ref={ref}
          >
            <option disabled value="" hidden>
              {placeholder || 'Select an option'}
            </option>
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled || false}>
                {option.label}
              </option>
            ))}
          </select>
          <Iconly name="ChevronDown" set="light" className="pointer-events-none absolute right-2 top-1/2 h-5 -translate-y-1/2" />
        </div>
      )}
      {props.disabled && (
        <span
          aria-disabled="true"
          className={classNames(
            'rounded-full border border-th-border px-4 py-2 font-body text-sm',
            'bg-th-content text-th-text enabled:cursor-pointer',
            className
          )}
        >
          {(() => {
            const effectiveValue = (value || selected || (rest.value as string) || '').toString()
            const match = options.find((option) => option.value === effectiveValue)
            if (match) return match.label
            return placeholder || 'Not specified'
          })()}
        </span>
      )}
      {info && <div className="text-sm text-th-info">{info}</div>}
    </div>
  )
})

export default Select
